import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import {
  CheckIcon,
  PaperClipIcon,
  PencilIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import AdminSidebar from "../../../components/AdminSidebar";
import Pagination from "../../../components/Pagination";

export default function ApproveClients() {
  const [clients, setClients] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const user = getUser();
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const fetchClients = async () => {
    try {
      const response = await axiosInstance.get(
        window.REACT_APP_API_ENDPOINT +
          "/clients/?status=Pending&page=" +
          currentPage
      );
      setClients(response.data.data);
      setError(null);
    } catch (error) {
      setError("Failed to fetch clients. Please try again.");
      console.error("Fetch clients error:", error);
    }
  };

  const searchClients = async (event) => {
    if (event.key === "Enter") {
      try {
        const response = await axiosInstance.get(
          window.REACT_APP_API_ENDPOINT + "/clients/search?q=" + searchQuery
        );
        setClients(response.data);
        setError(null);
      } catch (error) {
        setError("Failed to search clients. Please try again.");
        console.error("search clients error:", error);
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(
        `${window.REACT_APP_API_ENDPOINT}/clients/${id}`
      );
      setClients(clients.filter((client) => client.id !== id));
      setError(null);
    } catch (error) {
      setError("Failed to delete client. Please try again.");
      console.error("Delete client error:", error);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/clients/${id}/status?status=` + status
      );
      fetchClients();
      setError(null);
    } catch (error) {
      setError("Failed to update client status. Please try again.");
      console.error("Update client status error:", error);
    }
  };

  const handlePageDataUpdate = (data) => {
    setClients(data);
  };

  if (!clients) {
    return <p>Loading...</p>;
  }

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
            <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
          </div>
        </div>
        <div className="mt-8 flow-root bg-white rounded-lg">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
                <h4 className="font-semibold text-2xl mb-2 lg:mb-0">
                  Pending Clients
                </h4>
                <input
                  type="text"
                  name="search"
                  id="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyUp={searchClients}
                  className="mb-2 lg:mb-0 block w-70 rounded-md border-0 py-1.5 pr-14 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                <div className="flex items-center">
                  <a
                    href="/admin/clients"
                    className="bg-bluePrimary text-white rounded-md px-4 py-3 font-bold mr-4"
                  >
                    Clients
                  </a>
                  <a
                    href="/admin/clients/form"
                    className="border border-bluePrimary text-bluePrimary rounded-md px-4 py-3 font-bold"
                  >
                    Register Client +
                  </a>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-black">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        Full Name
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-white"
                      >
                        Email
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-white"
                      >
                        Phone
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-white"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-0"
                      >
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {clients.map((client) => (
                      <tr key={client.id}>
                        <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                          <div className="flex items-center pl-4">
                            <div className="">
                              <div className="font-medium text-gray-900">
                                {client.name} {client.last_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <div className="mt-1 text-black">{client.email}</div>
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <div className="mt-1 text-black">{client.phone}</div>
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                            {client.status}
                          </span>
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                          <div className="flex items-center pl-4">
                            <button
                              onClick={() =>
                                handleStatusChange(client.id, "Approved")
                              }
                            >
                              <CheckIcon
                                className="h-5 w-5 flex-shrink-0 text-gray-400"
                                aria-hidden="true"
                              />
                            </button>
                            <button
                              onClick={() =>
                                handleStatusChange(client.id, "Rejected")
                              }
                            >
                              <XMarkIcon
                                className="h-5 w-5 flex-shrink-0 text-gray-400"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                apiEndpoint={window.REACT_APP_API_ENDPOINT + "/clients"}
                onDataUpdate={handlePageDataUpdate}
                customQueryParams="&status=Pending"
              />
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}
